import toast from 'react-hot-toast';
import { useCallback, useEffect, useRef } from 'react';

import { api } from '@/utils/api';

/** How long to keep watching after a connect/copy. Hosted auth runs in a popup we
 *  get no callback from, and the worker task behind it re-hosts a photo and can
 *  run the background-removal model, so the result lands well after the mutation
 *  resolves. Both entry points wait the same amount — the copy path does strictly
 *  MORE work than the connect path, so it must not wait less. */
const LINKEDIN_CONNECT_POLL_MS = 120_000;

/**
 * Returns a `start()` that refetches the queries backing a LinkedIn connection
 * every 2s for two minutes.
 *
 * The timers are owned by an effect, so they are cleared when the component
 * unmounts and a second `start()` replaces the first instead of stacking on it.
 * That matters because the surfaces calling this — the settings Automation tab
 * and the reconnect banner — are exactly where a user clicks twice: a bare
 * `setInterval` per click would leave N overlapping loops each invalidating five
 * queries, still firing after navigation.
 */
function useLinkedInConnectionPoll(): () => void {
  const utils = api.useUtils();
  const timers = useRef<{ interval?: ReturnType<typeof setInterval>; timeout?: ReturnType<typeof setTimeout> }>({});

  const stop = useCallback(() => {
    if (timers.current.interval != null) clearInterval(timers.current.interval);
    if (timers.current.timeout != null) clearTimeout(timers.current.timeout);
    timers.current = {};
  }, []);

  // Unmount is the only thing this effect does — `start` is imperative because it
  // is triggered by a mutation result, not by rendered state.
  useEffect(() => stop, [stop]);

  return useCallback(() => {
    stop();
    timers.current.interval = setInterval(() => {
      void utils.user.me.invalidate();
      void utils.team.teamUser.invalidate();
      void utils.team.teamUserWithAll.invalidate();
      void utils.user.getProxyAssignment.invalidate();
      void utils.aiStep.fetchNext.invalidate();
    }, 2000);
    timers.current.timeout = setTimeout(stop, LINKEDIN_CONNECT_POLL_MS);
  }, [stop, utils]);
}

export const useUpdateTeamData = () => {
  const utils = api.useUtils();

  return api.user.updateTeamData.useMutation({
    onSuccess: async (res) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.teamUser.invalidate({ teamId: res.teamId, userId: res.userId }),
        utils.team.teamUserWithAll.invalidate({ teamId: res.teamId, userId: res.userId }),
        utils.superAdmin.doneForYouUsers.invalidate(),
      ]);
    },
  });
};

export const useUnipileAuthMutation = () => {
  const startPolling = useLinkedInConnectionPoll();

  return api.user.unipileGenAuthUrl.useMutation({
    onSuccess: () => {
      // The popup owns the rest of the flow, and on the already-healthy path a
      // worker task is refreshing the profile — watch for both.
      startPolling();
    },
    onError: (error) => {
      // Show user-friendly toast for Unipile errors (e.g., API token invalid, service unavailable)
      // The backend already classifies errors and returns appropriate messages
      toast.error(error.message);
    },
  });
};

export const useUnipileCopyFromTeamMutation = () => {
  const utils = api.useUtils();
  const startPolling = useLinkedInConnectionPoll();

  return api.user.unipileCopyFromTeam.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.teamUser.invalidate(),
        // The automation card reads unipileAccountId off teamUserWithAll — invalidate it so
        // the card flips to "Connected". Refresh the copy-from-team list (this team drops off
        // it) and the routing line (the copied account may route through a proxy).
        utils.team.teamUserWithAll.invalidate(),
        utils.user.unipileOtherTeamsConnected.invalidate(),
        utils.user.getProxyAssignment.invalidate(),
      ]);
      // The connection is copied synchronously, but this team's own LinkedIn
      // profile row + avatar are filled in by a worker task right after — which
      // may run the background-removal model, so this waits as long as connect.
      startPolling();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useUnipileDisconnectMutation = () => {
  const utils = api.useUtils();

  return api.user.unipileDisconnect.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.teamUser.invalidate(),
        // The card reads unipileAccountId off teamUserWithAll — invalidate it so the card flips
        // back to the disconnected state. Refresh the copy-from-team list (now shown again) and
        // the routing line (no longer connected).
        utils.team.teamUserWithAll.invalidate(),
        utils.user.unipileOtherTeamsConnected.invalidate(),
        utils.user.getProxyAssignment.invalidate(),
      ]);
    },
    onError: (error) => {
      // Show user-friendly toast for Unipile disconnect errors
      toast.error(error.message);
    },
  });
};

export const useUpdateTeamThrottlingData = () => {
  const utils = api.useUtils();

  return api.user.updateUserTeamThrottlingData.useMutation({
    onSuccess: async (res) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.teamUser.invalidate({ teamId: res.teamId, userId: res.userId }),
        utils.team.teamUserWithAll.invalidate({ teamId: res.teamId, userId: res.userId }),
      ]);
    },
  });
};

export const useUpdateProfilePicture = () => {
  const utils = api.useUtils();

  return api.user.updateProfilePicture.useMutation({
    onSuccess: (res) => {
      const teamId = res.teamId;
      const userId = res.userId;

      utils.user.me.setData(undefined, (data) => {
        if (data && data.userTeam && data.userTeam.teamId == res.teamId && data.userTeam.userId == res.userId) {
          return {
            ...data,
            userTeam: {
              ...data.userTeam,
              profilePicture: res.profilePicture,
            },
          };
        }
        return data;
      });
      utils.team.teamUser.setData({ teamId, userId }, (data) => {
        if (data && data.teamId == res.teamId && data.userId == res.userId) {
          return {
            ...data,
            profilePicture: res.profilePicture,
          };
        }
        return data;
      });
      // The cutout variants live on teamUserWithAll's profile data — invalidate
      // so the nobg/trimmed images generated on upload appear on the settings page.
      void utils.team.teamUserWithAll.invalidate({ teamId, userId });
    },
  });
};

export const useRegenerateProfilePictureCutouts = () => {
  const utils = api.useUtils();

  return api.user.regenerateProfilePictureCutouts.useMutation({
    onSuccess: (res) => {
      // The cutout variants are read off teamUserWithAll's profile data —
      // invalidate so the freshly generated nobg/trimmed images show.
      void utils.team.teamUserWithAll.invalidate({ teamId: res.teamId, userId: res.userId });
    },
  });
};

export const useMeUpdateMutation = () => {
  const utils = api.useUtils();

  return api.user.meUpdate.useMutation({
    onSuccess: async () => {
      await utils.user.me.invalidate();
    },
  });
};

export const useRecordSignUpDataMutation = () => {
  const utils = api.useUtils();

  return api.user.recordSignUpData.useMutation({
    onSuccess: async () => {
      await utils.user.me.invalidate();
    },
  });
};

export const useSuperadminMeDeleteFromTeamMutation = () => {
  const utils = api.useUtils();

  return api.superAdmin.deleteMeFromTeam.useMutation({
    onSuccess: async (_, teamId) => {
      await utils.superAdmin.teamId.invalidate(teamId);
      void utils.superAdmin.allUsers.invalidate();
    },
  });
};

export const useSuperadminMeAddToTeamMutation = () => {
  const utils = api.useUtils();

  return api.superAdmin.addMeToTeam.useMutation({
    onSuccess: async (_, teamId) => {
      await utils.superAdmin.teamId.invalidate(teamId);
    },
  });
};

export const useUpdateEmailCredentials = () => {
  const utils = api.useUtils();

  return api.team.updateEmailCredential.useMutation({
    onSuccess: async (_, { teamId }) => {
      await utils.team.emailCredentials.invalidate({ teamId });
    },
  });
};

export const useTestEmailCredentials = () => {
  const utils = api.useUtils();

  return api.team.testEmailCredential.useMutation({
    onSuccess: async () => {
      await utils.team.emailCredentials.invalidate();
    },
  });
};

export const useDeleteEmailCredentials = () => {
  const utils = api.useUtils();

  return api.team.deleteEmailCredential.useMutation({
    onSuccess: async (_, { teamId }) => {
      await utils.team.emailCredentials.invalidate({ teamId });
    },
  });
};

export const useDisconnectLinkedin = () => {
  const utils = api.useUtils();

  return api.user.disconnectLinkedin.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.team.teamUser.invalidate(), utils.user.me.invalidate()]);
      void utils.team.teamUserWithAll.invalidate();
    },
  });
};

export const useUpsertRunCalendarBulk = () => {
  const utils = api.useUtils();

  return api.user.upsertCalendarBulk.useMutation({
    onSuccess: () => {
      void utils.team.teamUser.invalidate();
      void utils.user.me.invalidate();
      void utils.user.getCalendar.invalidate();
    },
  });
};

export const useUpdateRunCalendar = () => {
  const utils = api.useUtils();

  return api.user.updateCalendar.useMutation({
    onSuccess: () => {
      void utils.user.getCalendar.invalidate();
    },
  });
};

export const useCreateAudioDropFile = () => {
  const utils = api.useUtils();

  return api.user.createAudioDropFile.useMutation({
    onSuccess: () => {
      void utils.user.getAudioDropFiles.invalidate();
    },
  });
};

export const useDeleteAudioDropFile = () => {
  const utils = api.useUtils();

  return api.user.deleteAudioDropFile.useMutation({
    onSuccess: () => {
      void utils.user.getAudioDropFiles.invalidate();
    },
  });
};
